import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  format, addDays, subDays,
  parseISO, differenceInDays, isToday, isWeekend,
  eachDayOfInterval, isBefore, isAfter
} from 'date-fns';
import { es as dfEs, fi as dfFi, sv as dfSv, de as dfDe } from 'date-fns/locale';
import { useT, useLang } from '@/i18n';

const DATE_FNS_LOCALES = { es: dfEs, fi: dfFi, sv: dfSv, de: dfDe };

// Width in px of each day column
const CELL_W = 46;
// Height in px of each property row
const ROW_H = 38;
// Width of the sticky left property column
const LEFT_W = 190;

// Status colour map
const STATUS = {
  CONFIRMED: { bar: '#4a90a4', text: '#ffffff', border: '#3a7a8e' },
  PENDING:   { bar: '#f59e0b', text: '#ffffff', border: '#d97706' },
  CANCELLED: { bar: '#ef4444', text: '#ffffff', border: '#dc2626' },
  BLOCKED:   { bar: '#94a3b8', text: '#ffffff', border: '#7c8fa3' },
};

/* ─── helpers ──────────────────────────────────────────────────────────── */

/** Return {left, width} in px if event overlaps with [rangeStart, rangeEnd], else null */
function barGeometry(startDateStr, endDateStr, rangeStart, daysToShow) {
  const s = parseISO(startDateStr);
  const e = parseISO(endDateStr);
  const rangeEnd = addDays(rangeStart, daysToShow - 1);

  if (isAfter(s, rangeEnd) || isBefore(e, rangeStart)) return null;

  const startOff = Math.max(0, differenceInDays(s, rangeStart));
  const endOff   = Math.min(daysToShow - 1, differenceInDays(e, rangeStart));

  return {
    left:  startOff * CELL_W + 2,
    width: (endOff - startOff + 1) * CELL_W - 4,
  };
}

/* ─── DateHeader ────────────────────────────────────────────────────────── */

const DateHeader = ({ days, dfLocale }) => {
  // Group days by month for the top label row
  const monthGroups = useMemo(() => {
    const groups = [];
    let cur = null;
    days.forEach((day) => {
      const label = format(day, 'MMM yyyy', { locale: dfLocale });
      if (!cur || cur.label !== label) {
        cur = { label, count: 1 };
        groups.push(cur);
      } else {
        cur.count++;
      }
    });
    return groups;
  }, [days, dfLocale]);

  return (
    // No marginLeft: LEFT_W here — the sticky headers below (lines ~546 and
    // ~587) already render a LEFT_W-wide corner cell alongside <DateHeader>,
    // so adding it again shifts the day cells another LEFT_W right. Bars in
    // PropertyRow start at pixel LEFT_W (right of the sticky name column) but
    // day cells landed at 2*LEFT_W, so every bar appeared exactly
    // LEFT_W / CELL_W ≈ 4 days earlier than its correct date column. Popup
    // shows the right dates because it formats parseISO(...) directly and
    // never touches the horizontal layout.
    <div className="flex flex-col select-none">
      {/* Month row */}
      <div className="flex border-b border-[#194a50]/10">
        {monthGroups.map((mg, i) => (
          <div
            key={i}
            className="text-xs font-semibold text-[#0c2431] px-2 py-0.5 border-r border-[#194a50]/5 bg-[#f4f7f8]"
            style={{ width: mg.count * CELL_W, minWidth: mg.count * CELL_W }}
          >
            {mg.label}
          </div>
        ))}
      </div>
      {/* Day number + weekday row */}
      <div className="flex">
        {days.map((day) => {
          const today    = isToday(day);
          const weekend  = isWeekend(day);
          return (
            <div
              key={day.toISOString()}
              className="flex flex-col items-center justify-center border-r border-[#194a50]/5 py-1"
              style={{
                width: CELL_W, minWidth: CELL_W,
                background: today ? 'rgba(216,162,140,0.18)' : weekend ? 'rgba(25,74,80,0.05)' : '#f4f7f8',
              }}
            >
              <span
                className="text-xs font-bold leading-none"
                style={{ color: today ? '#d8a28c' : '#0c2431' }}
              >
                {format(day, 'd', { locale: dfLocale })}
              </span>
              <span
                className="text-[10px] leading-none mt-0.5"
                style={{ color: today ? '#d8a28c' : 'rgba(25,74,80,0.45)' }}
              >
                {format(day, 'EEE', { locale: dfLocale })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── PropertyRow ───────────────────────────────────────────────────────── */

const PropertyRow = ({ property, bookings, blocks, days, rangeStart, daysToShow, isOwner, isEven, onBookingClick, t }) => {
  const bgRow = isEven ? '#ffffff' : '#f6fafb';

  const guestLabel = (booking) => {
    if (isOwner) return t('calendar.page.reserved');
    const g = booking.guests?.[0];
    if (g) return `${g.first_name || ''} ${g.last_name || ''}`.trim();
    return booking.reference || '';
  };

  return (
    <div className="flex border-b border-[#194a50]/5 group" style={{ height: ROW_H }}>
      {/* Sticky property name */}
      <div
        className="sticky left-0 z-10 flex items-center px-3 border-r border-[#194a50]/10 shrink-0"
        style={{ width: LEFT_W, minWidth: LEFT_W, background: bgRow }}
      >
        <span
          className="text-xs font-medium text-[#194a50] truncate"
          title={`${property.reference ? property.reference + ', ' : ''}${property.title || ''}`}
        >
          {property.reference
            ? `${property.reference}, ${(property.title || '').split(',')[0]}`
            : property.title}
        </span>
      </div>

      {/* Timeline strip */}
      <div className="relative flex-1" style={{ height: ROW_H, background: bgRow }}>
        {/* Grid lines per day */}
        <div className="absolute inset-0 flex pointer-events-none">
          {days.map((day) => (
            <div
              key={day.toISOString()}
              className="border-r border-[#194a50]/5 h-full shrink-0"
              style={{
                width: CELL_W, minWidth: CELL_W,
                background: isToday(day)
                  ? 'rgba(216,162,140,0.10)'
                  : isWeekend(day) ? 'rgba(25,74,80,0.025)' : 'transparent',
              }}
            />
          ))}
        </div>

        {/* Block bars */}
        {blocks.map((block, i) => {
          const geo = barGeometry(block.start_date, block.end_date, rangeStart, daysToShow);
          if (!geo) return null;
          const c = STATUS.BLOCKED;
          return (
            <div
              key={`blk-${i}`}
              className="absolute flex items-center px-2 rounded overflow-hidden"
              style={{
                top: 5, height: ROW_H - 10,
                left: geo.left, width: geo.width,
                background: c.bar, borderLeft: `2px solid ${c.border}`,
              }}
              title={t('calendar.page.external_block')}
            >
              <span className="text-[10px] text-white font-medium truncate">{t('calendar.page.block_label')}</span>
            </div>
          );
        })}

        {/* Booking bars */}
        {bookings.map((booking, i) => {
          const geo = barGeometry(booking.start_date, booking.end_date, rangeStart, daysToShow);
          if (!geo) return null;
          const c = STATUS[booking.status] || STATUS.CONFIRMED;
          return (
            <div
              key={`bk-${i}`}
              className="absolute flex items-center px-2 rounded overflow-hidden cursor-pointer hover:brightness-90 transition-all"
              style={{
                top: 5, height: ROW_H - 10,
                left: geo.left, width: geo.width,
                background: c.bar, borderLeft: `2px solid ${c.border}`,
              }}
              title={`${booking.reference} — ${guestLabel(booking)}`}
              onClick={() => onBookingClick(booking)}
            >
              <span className="text-[10px] font-semibold text-white truncate">
                {booking.reference}{' '}
                <span className="font-normal opacity-90">{guestLabel(booking)}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── BookingPopup ──────────────────────────────────────────────────────── */

const BookingPopup = ({ booking, isOwner, onClose, t, dfLocale, navigate }) => {
  const c = STATUS[booking.status] || STATUS.CONFIRMED;
  const g = booking.guests?.[0];
  const guestName = isOwner
    ? t('calendar.page.reserved')
    : g ? `${g.first_name || ''} ${g.last_name || ''}`.trim() : '—';

  const nights = booking.start_date && booking.end_date
    ? differenceInDays(parseISO(booking.end_date), parseISO(booking.start_date))
    : '?';

  const statusLabel = {
    CONFIRMED: t('calendar.page.status_confirmed'),
    PENDING:   t('calendar.page.status_pending'),
    CANCELLED: t('calendar.page.status_cancelled'),
    BLOCKED:   t('calendar.page.status_blocked'),
  }[booking.status] || booking.status;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header stripe */}
        <div className="px-6 py-4 flex items-center justify-between" style={{ background: c.bar }}>
          <div>
            <p className="text-white/70 text-xs font-medium uppercase tracking-wider">{t('calendar.page.popup_booking')}</p>
            <p className="text-white text-xl font-bold font-['Outfit']">{booking.reference}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-3">
          <Row label={t('calendar.page.popup_status')}>
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full text-white"
              style={{ background: c.bar }}
            >
              {statusLabel}
            </span>
          </Row>
          <Row label={t('calendar.page.popup_property')}>{booking.property_title || '—'}</Row>
          <Row label={t('calendar.page.popup_checkin')}>{booking.start_date ? format(parseISO(booking.start_date), 'EEE, dd MMM yyyy', { locale: dfLocale }) : '—'}</Row>
          <Row label={t('calendar.page.popup_checkout')}>{booking.end_date ? format(parseISO(booking.end_date), 'EEE, dd MMM yyyy', { locale: dfLocale }) : '—'}</Row>
          <Row label={t('calendar.page.popup_nights')}>{nights}</Row>
          {!isOwner && <Row label={t('calendar.page.popup_guest')}>{guestName}</Row>}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex gap-2">
          <Button
            className="flex-1 rounded-full text-sm text-white"
            style={{ background: '#194a50' }}
            onClick={() => { navigate(`/bookings/${booking.id}`); }}
          >
            {t('calendar.page.view_full_booking')}
          </Button>
          <Button variant="outline" className="rounded-full text-sm" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-xs text-[#194a50]/50 font-medium whitespace-nowrap">{label}</span>
    <span className="text-sm text-[#0c2431] font-medium text-right">{children}</span>
  </div>
);

/* ─── CalendarPage (main) ───────────────────────────────────────────────── */

const CalendarPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === 'OWNER';
  const t = useT();
  const [lang] = useLang();
  const dfLocale = DATE_FNS_LOCALES[lang];

  const [properties, setProperties]   = useState([]);
  const [allEvents, setAllEvents]     = useState({ bookings: [], blocks: [] });
  const [loading, setLoading]         = useState(true);
  const [daysToShow, setDaysToShow]   = useState(14);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // rangeStart: start of visible window (today by default)
  const [rangeStart, setRangeStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Custom date range state
  const [customMode, setCustomMode] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');

  const rangeEnd = addDays(rangeStart, daysToShow - 1);
  const days     = eachDayOfInterval({ start: rangeStart, end: rangeEnd });

  /* fetch property list once */
  useEffect(() => { fetchProperties(); }, []);

  /* fetch calendar data whenever the visible window changes */
  useEffect(() => {
    fetchAllCalendarData();
  }, [rangeStart, daysToShow]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProperties = async () => {
    try {
      const res = await axios.get(`${API}/properties`);
      setProperties(res.data.items || []);
    } catch {
      toast.error(t('calendar.page.toast_properties_failed'));
    }
  };

  const fetchAllCalendarData = async () => {
    setLoading(true);
    try {
      const start = format(rangeStart, 'yyyy-MM-dd');
      const end   = format(rangeEnd,   'yyyy-MM-dd');
      const res   = await axios.get(`${API}/calendar/aggregated`, {
        params: { start_date: start, end_date: end },
      });
      const events   = res.data.events || [];
      const bookings = res.data.bookings
        || events.filter((e) => e.type === 'booking' || e.type === 'reserved');
      const blocks   = res.data.external_blocks
        || events.filter((e) => e.type === 'external_block' || e.type === 'blocked');
      setAllEvents({ bookings, blocks });
    } catch {
      toast.error(t('calendar.page.toast_calendar_failed'));
    } finally {
      setLoading(false);
    }
  };

  /* Group events by property_id */
  const eventsByProperty = useMemo(() => {
    const map = {};
    properties.forEach((p) => { map[p.id] = { bookings: [], blocks: [] }; });
    allEvents.bookings.forEach((b) => {
      if (b.property_id && map[b.property_id]) map[b.property_id].bookings.push(b);
    });
    allEvents.blocks.forEach((b) => {
      if (b.property_id && map[b.property_id]) map[b.property_id].blocks.push(b);
    });
    return map;
  }, [allEvents, properties]);

  /* Booking count summary for current window */
  const totalConfirmed = allEvents.bookings.filter((b) => b.status === 'CONFIRMED').length;
  const totalPending   = allEvents.bookings.filter((b) => b.status === 'PENDING').length;

  return (
    <div className="flex flex-col gap-4 animate-fade-in" data-testid="calendar-page">

      {/* ── Page header ─────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">{t('calendar.title')}</h1>
          <p className="text-[#194a50]/60 text-sm mt-0.5">
            {t('calendar.page.subtitle')}
          </p>
        </div>

        {/* Summary pills */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-[#4a90a4]/10 text-[#4a90a4] px-3 py-1.5 rounded-full text-xs font-semibold">
            <div className="w-2 h-2 rounded-full bg-[#4a90a4]" />
            {t('calendar.page.confirmed_count', { n: totalConfirmed })}
          </div>
          {totalPending > 0 && (
            <div className="flex items-center gap-1.5 bg-amber-100 text-amber-600 px-3 py-1.5 rounded-full text-xs font-semibold">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              {t('calendar.page.pending_count', { n: totalPending })}
            </div>
          )}
        </div>
      </div>

      {/* ── Navigation bar ──────────────────────────────────────── */}
      <div className="flex items-center justify-between bg-white border border-[#194a50]/10 rounded-xl px-4 py-2.5 shadow-sm gap-4">
        {/* Previous */}
        <Button
          variant="ghost" size="icon"
          onClick={() => setRangeStart((p) => subDays(p, daysToShow))}
          className="rounded-full w-9 h-9 hover:bg-[#194a50]/8 shrink-0"
        >
          <ChevronLeft className="w-5 h-5 text-[#194a50]" />
        </Button>

        {/* Period label + Today */}
        <div className="flex items-center gap-3">
          <span className="text-base font-semibold text-[#0c2431] font-['Outfit'] whitespace-nowrap">
            {format(rangeStart, 'dd MMM', { locale: dfLocale })} — {format(rangeEnd, 'dd MMM yyyy', { locale: dfLocale })}
          </span>
          <Button
            variant="outline" size="sm"
            onClick={() => {
              const d = new Date(); d.setHours(0, 0, 0, 0);
              setRangeStart(d);
            }}
            className="rounded-full text-xs h-7 border-[#194a50]/30 text-[#194a50] hover:bg-[#194a50]/8 px-3"
          >
            {t('calendar.today')}
          </Button>
        </div>

        {/* Period selector */}
        <div className="flex items-center bg-[#f2f6f6] rounded-lg p-0.5 shrink-0">
          {[
            { label: '1W', days: 7 },
            { label: '2W', days: 14 },
            { label: '1M', days: 30 },
            { label: '3M', days: 90 },
            { label: '6M', days: 180 },
          ].map((opt) => (
            <button
              key={opt.label}
              onClick={() => { setDaysToShow(opt.days); setCustomMode(false); }}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                !customMode && daysToShow === opt.days
                  ? 'bg-white text-[#194a50] shadow-sm'
                  : 'text-[#194a50]/50 hover:text-[#194a50]'
              }`}
            >
              {opt.label}
            </button>
          ))}
          <button
            onClick={() => setCustomMode((v) => !v)}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
              customMode
                ? 'bg-white text-[#194a50] shadow-sm'
                : 'text-[#194a50]/50 hover:text-[#194a50]'
            }`}
          >
            {t('calendar.page.custom')}
          </button>
        </div>

        {/* Next */}
        <Button
          variant="ghost" size="icon"
          onClick={() => setRangeStart((p) => addDays(p, daysToShow))}
          className="rounded-full w-9 h-9 hover:bg-[#194a50]/8 shrink-0"
        >
          <ChevronRight className="w-5 h-5 text-[#194a50]" />
        </Button>
      </div>

      {/* ── Custom date picker row ──────────────────────────────── */}
      {customMode && (
        <div className="flex items-center gap-3 bg-white border border-[#194a50]/10 rounded-xl px-4 py-3 shadow-sm flex-wrap">
          <span className="text-sm font-medium text-[#194a50] shrink-0">{t('calendar.page.custom_range')}</span>
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#194a50]/60 whitespace-nowrap">{t('calendar.page.from')}</label>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-8 rounded-md border border-[#194a50]/20 px-2 text-sm text-[#0c2431] focus:outline-none focus:ring-1 focus:ring-[#194a50]/40"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#194a50]/60 whitespace-nowrap">{t('calendar.page.to')}</label>
              <input
                type="date"
                value={customTo}
                min={customFrom || undefined}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-8 rounded-md border border-[#194a50]/20 px-2 text-sm text-[#0c2431] focus:outline-none focus:ring-1 focus:ring-[#194a50]/40"
              />
            </div>
            <Button
              size="sm"
              disabled={!customFrom || !customTo || customFrom > customTo}
              onClick={() => {
                const start = new Date(customFrom);
                start.setHours(0, 0, 0, 0);
                const end = new Date(customTo);
                end.setHours(0, 0, 0, 0);
                const days = Math.max(1, Math.round((end - start) / 86400000) + 1);
                setRangeStart(start);
                setDaysToShow(days);
              }}
              className="bg-[#194a50] hover:bg-[#10555e] rounded-full text-xs h-8 px-4"
            >
              {t('calendar.page.apply')}
            </Button>
          </div>
          {customFrom && customTo && customFrom <= customTo && (
            <span className="text-xs text-[#194a50]/50 shrink-0">
              {t('calendar.page.days_count', { n: Math.round((new Date(customTo) - new Date(customFrom)) / 86400000) + 1 })}
            </span>
          )}
        </div>
      )}

      {/* ── Gantt timeline ──────────────────────────────────────── */}
      <div
        className="bg-white border border-[#194a50]/10 rounded-xl shadow-sm overflow-hidden"
        style={{ minHeight: 200 }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#194a50]" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div style={{ minWidth: LEFT_W + daysToShow * CELL_W }}>

              {/* ── Sticky date header (top) ─── */}
              <div
                className="sticky top-0 z-30 flex border-b border-[#194a50]/10"
                style={{ background: '#f4f7f8' }}
              >
                {/* Corner cell */}
                <div
                  className="sticky left-0 z-40 flex items-end px-4 pb-1.5 border-r border-[#194a50]/10 shrink-0"
                  style={{ width: LEFT_W, minWidth: LEFT_W, background: '#f4f7f8' }}
                >
                  <span className="text-[10px] font-semibold text-[#194a50]/50 uppercase tracking-wider">
                    {t('calendar.page.accommodation')}
                  </span>
                </div>
                <DateHeader days={days} dfLocale={dfLocale} />
              </div>

              {/* ── Property rows ─── */}
              {properties.length === 0 && (
                <div className="flex items-center justify-center h-32 text-sm text-[#194a50]/50">
                  {t('calendar.page.no_properties')}
                </div>
              )}
              {properties.map((property, idx) => {
                const evts = eventsByProperty[property.id] || { bookings: [], blocks: [] };
                return (
                  <PropertyRow
                    key={property.id}
                    property={property}
                    bookings={evts.bookings}
                    blocks={evts.blocks}
                    days={days}
                    rangeStart={rangeStart}
                    daysToShow={daysToShow}
                    isOwner={isOwner}
                    isEven={idx % 2 === 0}
                    onBookingClick={setSelectedBooking}
                    t={t}
                  />
                );
              })}

              {/* ── Repeated date header (bottom) for long lists ─── */}
              {properties.length > 12 && (
                <div
                  className="flex border-t border-[#194a50]/10"
                  style={{ background: '#f4f7f8' }}
                >
                  <div
                    className="sticky left-0 z-20 flex items-center px-4 border-r border-[#194a50]/10 shrink-0"
                    style={{ width: LEFT_W, minWidth: LEFT_W, background: '#f4f7f8' }}
                  >
                    <span className="text-[10px] font-semibold text-[#194a50]/50 uppercase tracking-wider">
                      {t('calendar.page.accommodation')}
                    </span>
                  </div>
                  <DateHeader days={days} dfLocale={dfLocale} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Legend ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-5 px-1">
        <span className="text-xs text-[#194a50]/50 font-medium">{t('calendar.page.legend')}</span>
        {[
          { key: 'confirmed', label: t('calendar.page.confirmed'), color: STATUS.CONFIRMED.bar },
          { key: 'pending',   label: t('calendar.page.pending'),   color: STATUS.PENDING.bar },
          { key: 'cancelled', label: t('calendar.page.cancelled'), color: STATUS.CANCELLED.bar },
          { key: 'blocked',   label: t('calendar.page.block_label'), color: STATUS.BLOCKED.bar },
        ].map((item) => (
          <div key={item.key} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ background: item.color }} />
            <span className="text-xs text-[#194a50]/60">{item.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded border-2 border-[#d8a28c] bg-[#d8a28c]/20" />
          <span className="text-xs text-[#194a50]/60">{t('calendar.today')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-[#194a50]/5 border border-[#194a50]/10" />
          <span className="text-xs text-[#194a50]/60">{t('calendar.page.weekend')}</span>
        </div>
      </div>

      {/* ── Booking detail popup ─────────────────────────────────── */}
      {selectedBooking && (
        <BookingPopup
          booking={selectedBooking}
          isOwner={isOwner}
          onClose={() => setSelectedBooking(null)}
          t={t}
          dfLocale={dfLocale}
          navigate={navigate}
        />
      )}
    </div>
  );
};

export default CalendarPage;
